<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { z } from 'zod';
import {
  UiAvatar,
  UiButton,
  UiForm,
  UiFormField,
  UiIcon,
  UiInputField,
  UiSelect,
  UiText,
  generateInitials,
  useApi,
  useSharedLink,
  type ExpertClaim,
  type SubmitExpertClaimRequest,
} from '@shopexperts/shared';
import { shopifyRoleOptions } from '@/utils/roles';
import LinkedInInputField from '@/app/components/claim-listing/LinkedInInputField.vue';
import { upsertClaimContext } from '@/app/composables/claim/claimContexts';
import { normalizeLinkedInUrl } from '@/app/composables/claim/linkedinUrl';
import { resolveEmbedParentOrigin } from '@/app/composables/claim/websiteOrigin';
import {
  extractServerMessage,
  extractStatus,
  mapValidationErrors,
  resolveThrottleMessage,
} from '@/app/composables/claim/claimApiErrors';
import { useResendClaimVerification } from '@/app/composables/claim/useResendClaimVerification';

interface ClaimExpertSummary {
  slug: string;
  name: string;
  type: 'agency' | 'freelancer';
  avatar?: string | null;
}

interface ClaimLinkData {
  expert: ClaimExpertSummary | null;
}

interface AgencyClaimFormValues {
  full_name: string;
  email: string;
  company_name: string;
  company_email: string;
  linkedin_url: string;
}

interface FreelancerClaimFormValues {
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  linkedin_url: string;
}

type ViewState = 'waiting' | 'unavailable' | 'form' | 'already-claimed' | 'duplicate' | 'conflict' | 'success';
type SubmissionErrorCode =
  | 'validation'
  | 'already-claimed'
  | 'duplicate-claim'
  | 'conflict'
  | 'throttled'
  | 'missing-expert'
  | 'unknown';

// The API's 409 responses carry no machine-readable code — only human-readable
// copy — so the two recovery states are routed by message matching. A
// structured error code in the API contract would remove this fragility.
const ALREADY_CLAIMED_MESSAGE = 'This profile has already been claimed.';
const DUPLICATE_CLAIM_MESSAGE = 'A claim for this profile with this email already exists.';
const GENERIC_SUBMIT_ERROR = 'Something went wrong while submitting your claim request. Please try again.';
const GENERIC_CONFLICT_MESSAGE = 'This listing can\'t be claimed right now.';
const INIT_TIMEOUT_MS = 3000;
const FORM_WIDTH = 650;
const SUCCESS_WIDTH = 500;

/** Tolerant compare so trivial copy drift (whitespace, casing) keeps routing. */
function matchesServerMessage(serverMessage: string, knownMessage: string): boolean {
  return serverMessage.trim().toLowerCase() === knownMessage.toLowerCase();
}

function requiredText(requiredMessage: string, max: number, maxMessage: string) {
  return z.preprocess(
    (value) => typeof value === 'string' ? value : '',
    z.string().trim().min(1, requiredMessage).max(max, maxMessage),
  );
}

function requiredEmail(requiredMessage: string) {
  return z.preprocess(
    (value) => typeof value === 'string' ? value : '',
    z.string()
      .trim()
      .min(1, requiredMessage)
      .email('Please enter a valid email address.')
      .max(255, 'Email must be 255 characters or fewer.'),
  );
}

const linkedInUrlSchema = z.preprocess(
  (value) => typeof value === 'string' ? value : '',
  z.string()
    .trim()
    .min(1, 'Please enter your LinkedIn profile URL.')
    .refine((value) => {
      const normalized = normalizeLinkedInUrl(value);
      return Boolean(normalized && normalized.length <= 255);
    }, 'Please enter a valid LinkedIn profile URL (linkedin.com).'),
);

const agencyClaimSchema = z.object({
  full_name: requiredText('Please enter your full name.', 200, 'Full name must be 200 characters or fewer.'),
  email: requiredEmail('Please enter your email address.'),
  company_name: requiredText('Please enter your company name.', 200, 'Company name must be 200 characters or fewer.'),
  company_email: requiredEmail('Please enter your company email address.'),
  linkedin_url: linkedInUrlSchema,
});

const freelancerClaimSchema = z.object({
  first_name: requiredText('Please enter your first name.', 100, 'First name must be 100 characters or fewer.'),
  last_name: requiredText('Please enter your last name.', 100, 'Last name must be 100 characters or fewer.'),
  email: requiredEmail('Please enter your email address.'),
  role: requiredText('Please select your role.', 100, 'Role must be 100 characters or fewer.'),
  linkedin_url: linkedInUrlSchema,
});

const route = useRoute();
const api = useApi();
const websiteOrigin = resolveEmbedParentOrigin(route.query.parentOrigin);
const contactUrl = `${websiteOrigin}/contact`;

const channelId = computed(() => {
  const value = route.query.channelId;
  return typeof value === 'string' && value ? value : 'claim-listing';
});

const { data: linkData, emitEvent } = useSharedLink<ClaimLinkData>({
  name: 'shopexperts-claim-listing',
  channelId: channelId.value,
  mode: 'bidirectional',
  targetOrigin: websiteOrigin,
  default: {
    expert: null,
  },
});

const view = ref<ViewState>('waiting');
const isSubmitting = ref(false);
const isFormDirty = ref(false);
const formError = ref('');
const serverErrors = ref<Record<string, string>>({});
const submittedClaim = ref<ExpertClaim | null>(null);
const duplicateEmail = ref('');
const conflictMessage = ref('');

const resend = useResendClaimVerification();

const agencyForm = ref<AgencyClaimFormValues>({
  full_name: '',
  email: '',
  company_name: '',
  company_email: '',
  linkedin_url: '',
});

const freelancerForm = ref<FreelancerClaimFormValues>({
  first_name: '',
  last_name: '',
  email: '',
  role: '',
  linkedin_url: '',
});

const expert = computed<ClaimExpertSummary | null>(() => {
  const value = linkData.value.expert;
  if (!value || typeof value.slug !== 'string' || !value.slug) return null;
  if (value.type !== 'agency' && value.type !== 'freelancer') return null;
  return value;
});
const expertInitials = computed(() => generateInitials(expert.value?.name ?? ''));
const expertTypeLabel = computed(() => expert.value?.type === 'agency' ? 'Agency' : 'Freelancer');
const isBusy = computed(() => isSubmitting.value);
const isDirty = computed(() => view.value === 'form' && isFormDirty.value);
const targetWidth = computed(() => view.value === 'success' ? SUCCESS_WIDTH : FORM_WIDTH);
const successEmail = computed(() => submittedClaim.value?.email ?? '');

watch(expert, (value) => {
  if (value && (view.value === 'waiting' || view.value === 'unavailable')) {
    view.value = 'form';
  }
}, { immediate: true });

watch([isDirty, isSubmitting], ([dirty, submitting]) => {
  emitEvent('dirty-change', { dirty, submitting });
});

watch([agencyForm, freelancerForm], () => {
  // Clear stale server feedback on edit. Guarded so the per-keystroke form
  // syncs don't churn these refs (reassignment used to postMessage a resize
  // per keystroke).
  if (formError.value) formError.value = '';
  if (Object.keys(serverErrors.value).length) serverErrors.value = {};
}, { deep: true });

// Explicit state changes re-measure; content-driven growth (error/resend text
// appearing) is already covered by the ResizeObserver on document.body.
watch([view, isSubmitting], () => emitResize());

function emitResize() {
  requestAnimationFrame(() => {
    const height = Math.ceil(document.body.scrollHeight || document.documentElement.scrollHeight || 641);
    emitEvent('resize', { height, width: targetWidth.value });
  });
}

function emitSubmissionError(code: SubmissionErrorCode, message: string, status?: number) {
  emitEvent('submission-error', { code, message, status });
}

async function handleAgencySubmit(values: AgencyClaimFormValues) {
  await submitClaim({
    full_name: values.full_name.trim(),
    company_name: values.company_name.trim(),
    company_email: values.company_email.trim(),
    email: values.email.trim(),
    linkedin_url: normalizeLinkedInUrl(values.linkedin_url) ?? values.linkedin_url.trim(),
  });
}

async function handleFreelancerSubmit(values: FreelancerClaimFormValues) {
  await submitClaim({
    first_name: values.first_name.trim(),
    last_name: values.last_name.trim(),
    role: values.role.trim(),
    email: values.email.trim(),
    linkedin_url: normalizeLinkedInUrl(values.linkedin_url) ?? values.linkedin_url.trim(),
  });
}

async function submitClaim(body: SubmitExpertClaimRequest) {
  if (isSubmitting.value) return;

  const claimedExpert = expert.value;
  if (!claimedExpert) {
    formError.value = 'We could not identify the selected expert. Please close this window and try again.';
    emitSubmissionError('missing-expert', formError.value);
    return;
  }

  formError.value = '';
  serverErrors.value = {};
  isSubmitting.value = true;

  try {
    const result = await api.public.expertClaims.submit({
      params: { slug: claimedExpert.slug },
      body,
    });

    upsertClaimContext({
      expert_slug: claimedExpert.slug,
      email: body.email,
      expert_name: claimedExpert.name,
    });

    submittedClaim.value = result.claim;
    view.value = 'success';
    emitEvent('submitted', result.claim);
  } catch (error) {
    handleSubmitError(error, body.email);
  } finally {
    isSubmitting.value = false;
  }
}

function handleSubmitError(error: unknown, email: string) {
  const status = extractStatus(error);
  const serverMessage = extractServerMessage(error);

  if (status === 422) {
    serverErrors.value = mapValidationErrors(error);
    formError.value = Object.keys(serverErrors.value).length
      ? 'Please check the highlighted fields and try again.'
      : serverMessage || GENERIC_SUBMIT_ERROR;
    emitSubmissionError('validation', formError.value, status);
    return;
  }

  if (status === 409) {
    if (matchesServerMessage(serverMessage, ALREADY_CLAIMED_MESSAGE)) {
      view.value = 'already-claimed';
      emitSubmissionError('already-claimed', serverMessage, status);
      return;
    }

    if (matchesServerMessage(serverMessage, DUPLICATE_CLAIM_MESSAGE)) {
      duplicateEmail.value = email;
      view.value = 'duplicate';
      emitSubmissionError('duplicate-claim', serverMessage, status);
      return;
    }

    // Unrecognized conflict copy (wording drift, new server guard): show the
    // server's message verbatim without guessing at a recovery affordance.
    conflictMessage.value = serverMessage || GENERIC_CONFLICT_MESSAGE;
    view.value = 'conflict';
    emitSubmissionError('conflict', conflictMessage.value, status);
    return;
  }

  if (status === 429) {
    formError.value = resolveThrottleMessage(error);
    emitSubmissionError('throttled', formError.value, status);
    return;
  }

  formError.value = serverMessage || GENERIC_SUBMIT_ERROR;
  emitSubmissionError('unknown', formError.value, status);
  console.error('[EmbeddedClaimListing] unknown submission error', error);
}

async function handleResendVerification() {
  const claimedExpert = expert.value;
  if (!claimedExpert || !duplicateEmail.value) return;
  await resend.send(claimedExpert.slug, duplicateEmail.value);
}

function handleCancel() {
  if (isBusy.value) return;
  emitEvent('cancel', { expert_slug: expert.value?.slug ?? '' });
}

let resizeObserver: ResizeObserver | null = null;
let initTimeout: ReturnType<typeof setTimeout> | null = null;

onMounted(() => {
  // The host clamps the dialog height to the viewport; on short viewports the
  // form must scroll inside the iframe so the footer stays reachable.
  document.documentElement.style.overflowY = 'auto';
  document.body.style.overflowY = 'auto';

  emitEvent('ready', { channelId: channelId.value });
  emitResize();

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => emitResize());
    resizeObserver.observe(document.body);
  }

  initTimeout = setTimeout(() => {
    if (view.value === 'waiting') {
      view.value = 'unavailable';
    }
  }, INIT_TIMEOUT_MS);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  if (initTimeout) clearTimeout(initTimeout);
});
</script>

<template>
  <main class="claim-embed bg-white font-archivo text-se-dark">
    <section
      class="relative mx-auto flex w-full flex-col rounded-lg bg-white p-6"
      :class="view === 'success' ? 'max-w-[500px] gap-4' : 'max-w-[650px] gap-6'"
      data-testid="embedded-claim-listing"
    >
      <div
        v-if="isBusy"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/85 px-6 text-center backdrop-blur-[1px]"
        data-testid="claim-busy-state"
      >
        <div class="h-8 w-8 animate-spin rounded-full border-2 border-se-gray-300 border-t-se-dark" />
        <UiText
          as="p"
          variant="override"
          text="Submitting your claim request..."
          class="text-[15px] font-medium leading-6 text-se-dark"
        />
      </div>

      <!-- Waiting for the host handshake / expert init data -->
      <div
        v-if="view === 'waiting'"
        class="flex min-h-[280px] flex-col items-center justify-center gap-3"
        role="status"
        data-testid="claim-waiting-state"
      >
        <div class="size-8 animate-spin rounded-full border-2 border-se-gray-300 border-t-se-dark" />
        <UiText as="p" variant="p" text="Loading claim form..." />
      </div>

      <!-- Init data never arrived (direct open / handshake failure) -->
      <div
        v-else-if="view === 'unavailable'"
        class="flex min-h-[280px] flex-col items-center justify-center gap-2 text-center"
        data-testid="claim-unavailable-state"
      >
        <UiText
          as="h1"
          variant="override"
          text="We couldn't load the claim form"
          class="text-[24px] font-medium leading-8 text-se-dark"
        />
        <UiText
          as="p"
          variant="override"
          text="Please close this window and reopen the claim form from the expert's profile page."
          class="text-[16px] font-normal leading-6 text-se-dark"
        />
      </div>

      <!-- Success state (Figma 1550:6311, 500px wide) -->
      <template v-else-if="view === 'success'">
        <UiIcon name="check-circle-broken" class="size-16 shrink-0 text-se-green" />
        <div class="flex w-full flex-col gap-2" data-testid="claim-success-state">
          <UiText
            as="h1"
            variant="override"
            text="Your claim request is submitted!"
            class="text-[24px] font-medium leading-8 text-se-dark"
          />
          <UiText as="p" variant="override" class="text-[16px] font-normal leading-6 text-se-dark">
            We've sent a verification link to <span class="font-semibold">{{ successEmail }}</span> — please check
            your inbox and verify your email. Our team will then review your claim request. You can expect a
            response within 2-3 business days.
          </UiText>
        </div>
      </template>

      <!-- Form + informational replacement states share the modal chrome -->
      <template v-else>
        <header class="flex flex-col gap-2">
          <UiText
            as="h1"
            variant="override"
            text="Claim this listing"
            class="text-[24px] font-medium leading-8 text-se-dark"
          />
          <UiText
            as="p"
            variant="override"
            text="Take full control, boost your visibility, and start attracting high-quality leads."
            class="text-[16px] font-normal leading-6 text-se-dark"
          />
        </header>

        <div
          v-if="view === 'form'"
          class="flex w-full items-center gap-2 rounded-lg bg-[rgba(0,122,255,0.2)] p-2"
        >
          <UiIcon name="info-circle-color" class="size-4 shrink-0" />
          <UiText
            as="p"
            variant="override"
            text="Only the company owner or an authorized employee can claim this listing."
            class="text-[14px] font-normal leading-[22px] text-se-dark"
          />
        </div>

        <section
          v-if="expert"
          class="flex w-full items-center gap-4 rounded-lg border border-se-gray-300 bg-white px-4 py-2"
        >
          <UiAvatar
            :url="expert.avatar ?? undefined"
            :initials="expertInitials"
            :name="expert.name"
            size="xs"
            :responsive="false"
            class="size-12"
          />
          <div class="flex min-w-0 flex-col">
            <UiText
              as="span"
              variant="override"
              :text="expert.name"
              class="truncate text-[16px] font-semibold leading-6 text-se-dark"
            />
            <UiText
              as="span"
              variant="override"
              :text="expertTypeLabel"
              class="truncate text-[14px] font-normal leading-[22px] text-se-dark"
            />
          </div>
        </section>

        <!-- 409: profile already claimed -->
        <template v-if="view === 'already-claimed'">
          <div
            class="flex w-full items-center gap-2 rounded-lg bg-[rgba(0,122,255,0.2)] p-2"
            data-testid="claim-already-claimed-state"
          >
            <UiIcon name="info-circle-color" class="size-4 shrink-0" />
            <UiText
              as="p"
              variant="override"
              text="This profile has already been claimed."
              class="text-[14px] font-normal leading-[22px] text-se-dark"
            />
          </div>
          <UiText as="p" variant="override" class="text-[16px] font-normal leading-6 text-se-dark">
            If you believe this is a mistake or you should be the owner of this listing, please get in touch with
            our team.
          </UiText>
          <UiText as="p" variant="override" class="text-[16px] leading-6 text-se-dark">
            Have questions?
            <a :href="contactUrl" target="_blank" rel="noopener" class="underline">Contact us</a>
          </UiText>
          <footer class="flex items-center justify-end">
            <UiButton type="button" variant="outline" :show-arrow="false" class="min-h-10! rounded-lg!" @click="handleCancel">
              <template #label>
                <span class="text-[16px] font-semibold leading-6">Close</span>
              </template>
            </UiButton>
          </footer>
        </template>

        <!-- 409: a claim with this email already exists -->
        <template v-else-if="view === 'duplicate'">
          <div class="flex flex-col gap-4" data-testid="claim-duplicate-state">
            <div class="flex w-full items-center gap-2 rounded-lg bg-[rgba(0,122,255,0.2)] p-2">
              <UiIcon name="info-circle-color" class="size-4 shrink-0" />
              <UiText
                as="p"
                variant="override"
                text="A claim with this email is already in progress."
                class="text-[14px] font-normal leading-[22px] text-se-dark"
              />
            </div>
            <UiText as="p" variant="override" class="text-[16px] font-normal leading-6 text-se-dark">
              If you haven't verified your email yet, you can resend the verification link; if you've already
              verified, no further action is needed.
            </UiText>
            <UiText
              v-if="resend.successMessage.value"
              as="p"
              variant="override"
              :text="resend.successMessage.value"
              class="text-[14px] leading-[22px] text-se-green"
              data-testid="claim-resend-success"
            />
            <UiText
              v-if="resend.errorMessage.value"
              as="p"
              variant="override"
              :text="resend.errorMessage.value"
              class="text-[14px] leading-[22px] text-se-danger"
              data-testid="claim-resend-error"
            />
            <UiText
              v-if="resend.cooldownSeconds.value > 0"
              as="p"
              variant="override"
              :text="`You can request another email in ${resend.cooldownSeconds.value}s.`"
              class="text-[14px] leading-[22px] text-se-gray-700"
            />
            <UiText as="p" variant="override" class="text-[16px] leading-6 text-se-dark">
              Have questions?
              <a :href="contactUrl" target="_blank" rel="noopener" class="underline">Contact us</a>
            </UiText>
            <footer class="flex items-center justify-between gap-4">
              <UiButton type="button" variant="outline" :show-arrow="false" class="min-h-10! rounded-lg!" @click="handleCancel">
                <template #label>
                  <span class="text-[16px] font-semibold leading-6">Close</span>
                </template>
              </UiButton>
              <UiButton
                type="button"
                variant="primary"
                :show-arrow="false"
                :disabled="!resend.canSend.value"
                class="min-h-10! rounded-lg!"
                data-testid="claim-resend-button"
                @click="handleResendVerification"
              >
                <template #label>
                  <span
                    class="text-[16px] font-semibold leading-6"
                    :class="resend.canSend.value ? 'bg-linear-to-b from-[#DCF1FD] to-white bg-clip-text text-transparent' : ''"
                  >Resend verification email</span>
                </template>
              </UiButton>
            </footer>
          </div>
        </template>

        <!-- 409 with unrecognized copy: server message verbatim, no recovery guesses -->
        <template v-else-if="view === 'conflict'">
          <div
            class="flex w-full items-center gap-2 rounded-lg bg-[rgba(0,122,255,0.2)] p-2"
            data-testid="claim-conflict-state"
          >
            <UiIcon name="info-circle-color" class="size-4 shrink-0" />
            <UiText
              as="p"
              variant="override"
              :text="conflictMessage"
              class="text-[14px] font-normal leading-[22px] text-se-dark"
            />
          </div>
          <UiText as="p" variant="override" class="text-[16px] leading-6 text-se-dark">
            Have questions?
            <a :href="contactUrl" target="_blank" rel="noopener" class="underline">Contact us</a>
          </UiText>
          <footer class="flex items-center justify-end">
            <UiButton type="button" variant="outline" :show-arrow="false" class="min-h-10! rounded-lg!" @click="handleCancel">
              <template #label>
                <span class="text-[16px] font-semibold leading-6">Close</span>
              </template>
            </UiButton>
          </footer>
        </template>

        <!-- Agency claim form -->
        <UiForm
          v-else-if="expert && expert.type === 'agency'"
          v-model="agencyForm"
          v-model:dirty="isFormDirty"
          :schema="agencyClaimSchema"
          :server-errors="serverErrors"
          data-testid="claim-agency-form"
          @submit="handleAgencySubmit"
        >
          <div class="flex flex-col gap-6">
            <div class="grid grid-cols-1 gap-6 min-[560px]:grid-cols-2 min-[560px]:gap-x-8">
              <UiFormField name="full_name" label="Full Name" :label-capitalize="false" type="text" class="claim-field">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                  <UiInputField
                    :id="id"
                    name="full_name"
                    :model-value="modelValue as string"
                    placeholder="Enter your full name"
                    :invalid="invalid"
                    :error="error"
                    :disabled="isBusy"
                    @update:model-value="onUpdateModelValue"
                    @validate="onValidate"
                  />
                </template>
              </UiFormField>

              <UiFormField
                name="email"
                label="Your email"
                inline-hint="(work email preferred)"
                inline-hint-placement="start"
                :label-capitalize="false"
                type="email"
                class="claim-field"
              >
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                  <UiInputField
                    :id="id"
                    name="email"
                    type="email"
                    :model-value="modelValue as string"
                    placeholder="yourname@company.com"
                    :invalid="invalid"
                    :error="error"
                    :disabled="isBusy"
                    @update:model-value="onUpdateModelValue"
                    @validate="onValidate"
                  />
                </template>
              </UiFormField>

              <UiFormField name="company_name" label="Company name" :label-capitalize="false" type="text" class="claim-field">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                  <UiInputField
                    :id="id"
                    name="company_name"
                    :model-value="modelValue as string"
                    placeholder="Enter company name"
                    :invalid="invalid"
                    :error="error"
                    :disabled="isBusy"
                    @update:model-value="onUpdateModelValue"
                    @validate="onValidate"
                  />
                </template>
              </UiFormField>

              <UiFormField name="company_email" label="Company email" :label-capitalize="false" type="email" class="claim-field">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                  <UiInputField
                    :id="id"
                    name="company_email"
                    type="email"
                    :model-value="modelValue as string"
                    placeholder="info@company.com"
                    :invalid="invalid"
                    :error="error"
                    :disabled="isBusy"
                    @update:model-value="onUpdateModelValue"
                    @validate="onValidate"
                  />
                </template>
              </UiFormField>

              <UiFormField
                name="linkedin_url"
                label="Your Linkedin Profile"
                :label-capitalize="false"
                type="url"
                class="claim-field min-[560px]:col-span-2"
              >
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                  <LinkedInInputField
                    :id="id"
                    name="linkedin_url"
                    :model-value="modelValue as string"
                    :invalid="invalid"
                    :error="error"
                    :disabled="isBusy"
                    @update:model-value="onUpdateModelValue"
                    @validate="onValidate"
                  />
                </template>
              </UiFormField>
            </div>

            <UiText
              v-if="formError"
              as="p"
              variant="override"
              :text="formError"
              class="text-[14px] leading-[22px] text-se-danger"
              data-testid="claim-form-error"
            />

            <UiText as="p" variant="override" class="text-[16px] leading-6 text-se-dark">
              Have questions?
              <a :href="contactUrl" target="_blank" rel="noopener" class="underline">Contact us</a>
            </UiText>

            <footer class="flex items-center justify-between gap-4">
              <UiButton
                type="button"
                variant="outline"
                :show-arrow="false"
                :disabled="isBusy"
                class="min-h-10! rounded-lg!"
                @click="handleCancel"
              >
                <template #label>
                  <span class="text-[16px] font-semibold leading-6">Cancel</span>
                </template>
              </UiButton>
              <UiButton
                type="submit"
                variant="primary"
                :show-arrow="false"
                :disabled="isBusy"
                class="min-h-10! rounded-lg!"
                data-testid="claim-submit-button"
              >
                <template #label>
                  <span
                    class="text-[16px] font-semibold leading-6"
                    :class="isBusy ? '' : 'bg-linear-to-b from-[#DCF1FD] to-white bg-clip-text text-transparent'"
                  >Send Claim Request</span>
                </template>
              </UiButton>
            </footer>
          </div>
        </UiForm>

        <!-- Freelancer claim form -->
        <UiForm
          v-else-if="expert && expert.type === 'freelancer'"
          v-model="freelancerForm"
          v-model:dirty="isFormDirty"
          :schema="freelancerClaimSchema"
          :server-errors="serverErrors"
          data-testid="claim-freelancer-form"
          @submit="handleFreelancerSubmit"
        >
          <div class="flex flex-col gap-6">
            <div class="grid grid-cols-1 gap-6 min-[560px]:grid-cols-2 min-[560px]:gap-x-8">
              <UiFormField name="first_name" label="First name" :label-capitalize="false" type="text" class="claim-field">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                  <UiInputField
                    :id="id"
                    name="first_name"
                    :model-value="modelValue as string"
                    placeholder="Enter your first name"
                    :invalid="invalid"
                    :error="error"
                    :disabled="isBusy"
                    @update:model-value="onUpdateModelValue"
                    @validate="onValidate"
                  />
                </template>
              </UiFormField>

              <UiFormField name="last_name" label="Last name" :label-capitalize="false" type="text" class="claim-field">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                  <UiInputField
                    :id="id"
                    name="last_name"
                    :model-value="modelValue as string"
                    placeholder="Enter your last name"
                    :invalid="invalid"
                    :error="error"
                    :disabled="isBusy"
                    @update:model-value="onUpdateModelValue"
                    @validate="onValidate"
                  />
                </template>
              </UiFormField>

              <UiFormField
                name="email"
                label="Your email"
                inline-hint="(work email preferred)"
                inline-hint-placement="start"
                :label-capitalize="false"
                type="email"
                class="claim-field"
              >
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                  <UiInputField
                    :id="id"
                    name="email"
                    type="email"
                    :model-value="modelValue as string"
                    placeholder="yourname@company.com"
                    :invalid="invalid"
                    :error="error"
                    :disabled="isBusy"
                    @update:model-value="onUpdateModelValue"
                    @validate="onValidate"
                  />
                </template>
              </UiFormField>

              <UiFormField name="role" label="Role" :label-capitalize="false" type="select" class="claim-field">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                  <div class="w-full" :class="{ 'claim-select-placeholder': !modelValue }">
                    <UiSelect
                      :id="id"
                      name="role"
                      :options="shopifyRoleOptions"
                      :model-value="modelValue as string"
                      placeholder="Select your role"
                      :invalid="invalid"
                      :error="error"
                      :disabled="isBusy"
                      @update:model-value="onUpdateModelValue"
                      @validate="onValidate"
                    />
                  </div>
                </template>
              </UiFormField>

              <UiFormField
                name="linkedin_url"
                label="Your Linkedin Profile"
                :label-capitalize="false"
                type="url"
                class="claim-field min-[560px]:col-span-2"
              >
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                  <LinkedInInputField
                    :id="id"
                    name="linkedin_url"
                    :model-value="modelValue as string"
                    :invalid="invalid"
                    :error="error"
                    :disabled="isBusy"
                    @update:model-value="onUpdateModelValue"
                    @validate="onValidate"
                  />
                </template>
              </UiFormField>
            </div>

            <UiText
              v-if="formError"
              as="p"
              variant="override"
              :text="formError"
              class="text-[14px] leading-[22px] text-se-danger"
              data-testid="claim-form-error"
            />

            <UiText as="p" variant="override" class="text-[16px] leading-6 text-se-dark">
              Have questions?
              <a :href="contactUrl" target="_blank" rel="noopener" class="underline">Contact us</a>
            </UiText>

            <footer class="flex items-center justify-between gap-4">
              <UiButton
                type="button"
                variant="outline"
                :show-arrow="false"
                :disabled="isBusy"
                class="min-h-10! rounded-lg!"
                @click="handleCancel"
              >
                <template #label>
                  <span class="text-[16px] font-semibold leading-6">Cancel</span>
                </template>
              </UiButton>
              <UiButton
                type="submit"
                variant="primary"
                :show-arrow="false"
                :disabled="isBusy"
                class="min-h-10! rounded-lg!"
                data-testid="claim-submit-button"
              >
                <template #label>
                  <span
                    class="text-[16px] font-semibold leading-6"
                    :class="isBusy ? '' : 'bg-linear-to-b from-[#DCF1FD] to-white bg-clip-text text-transparent'"
                  >Send Claim Request</span>
                </template>
              </UiButton>
            </footer>
          </div>
        </UiForm>
      </template>
    </section>
  </main>
</template>

<style scoped>
/* Figma: input text and placeholder are 16/24, placeholder #727171. */
.claim-embed :deep(input) {
  font-size: 16px;
  line-height: 24px;
}

.claim-embed :deep(input::placeholder) {
  color: #727171;
}

.claim-select-placeholder :deep([data-part='value-text']) {
  color: #727171;
}

/* Keep field errors from shifting the 24px row rhythm (exemplar convention). */
.claim-field :deep(.w-full.flex.flex-col.relative > .flex.flex-col.mt-1) {
  position: absolute;
  left: 0;
  top: 100%;
  margin-top: 2px;
  min-height: 16px;
}
</style>
